/**
 * Which file of a scan package is the GEOMETRY — the rule a viewer opens
 * (card `t_8ae1d20a`/E2 for the package, `t_92f7411f`/E3 for the dashboard).
 *
 * A package is the phone's capture folder, stored on the hub as a row plus its
 * files: the RoomPlan capture itself (`room-1.usdz`), the pipeline's converted
 * model (`model.glb`, once the cloud worker has run), the plan the pipeline
 * renders (`plan.png`), the parametric document (`scan.json` /
 * `room-1.scan.json`) and whatever else the capture wrote. Which of them a
 * 3D viewer opens is one rule, and this module is it:
 *
 *     `model.glb` → `model.usdz` → `structure.usdz` → any `.glb`/`.gltf` → any `.usdz`
 *
 * — the pipeline's GLB first (mesh plus per-layer colours), then the raw
 * capture, which three's own USD loader reads directly (measured on the real
 * package: 69 meshes, no textures, the same world bounds as the GLB).
 *
 * Two callers with one rule: the settings page downloads exactly this file, and
 * the dashboard's scan entry attaches exactly this file as the dashboard's
 * model. `services/scanApi.ts` re-exports it so both keep importing it from
 * where they always have.
 *
 * Pure on purpose: no stores, no transport, no Vue — a plain Node unit test
 * (`services/scanModelAttach.test.ts`) drives it.
 */

/** The file names of one package, as the hub describes them. */
export interface ScanPackageFileLike {
  name: string;
}

/** A scan row as this rule reads it (structural: the reader needs only names). */
export interface ScanPackageLike {
  files?: ScanPackageFileLike[];
}

/** The geometry a viewer opens, in the order the app prefers it. */
export function scanPrimaryModelName(scan: ScanPackageLike): string | null {
  const names = (scan.files ?? []).map((file) => file.name);
  const preferred = ['model.glb', 'model.usdz', 'structure.usdz'];
  for (const candidate of preferred) {
    if (names.includes(candidate)) return candidate;
  }
  return (
    names.find((name) => /\.(glb|gltf)$/i.test(name)) ?? names.find((name) => /\.usdz$/i.test(name)) ?? null
  );
}
